import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  Input,
  OnDestroy,
  ChangeDetectorRef,
  OnChanges, AfterViewInit, SimpleChanges, SimpleChange
} from '@angular/core';
import {ExportDropdownOptions} from '../../worklist/interfaces/export.config.interface';
import {ExportWorklistDataService} from '../services/export-worklist-data.service';
import {AuthenticationService} from "@ispm/user/services/authentication.service";
import {map, take, takeUntil} from "rxjs/operators";
import {Subject} from "rxjs";
import {ExportOptionStatusUpdate} from "../classes/export-options";
import {ActivityActions} from "@ispm/user/constants/activityActions";
import {ActivityService} from "@ispm/user/services/activity.service";
import { get} from 'lodash';
import {Observable} from "rxjs/Observable";

@Component({
  selector: 'gen-export-worklist-data',
  templateUrl: './export-worklist-data.component.html',
  styleUrls: ['./export-worklist-data.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})

export class ExportWorklistDataComponent implements OnInit, OnDestroy, OnChanges,  AfterViewInit {
  @Input() type: string;
  @Input() exportButtonText: string;
  @Input() exportOutSideClickClose: string;
  @Input() filterSelected:any;
  public disabled: boolean = false;
  public disabledByFilterSelection: boolean = false;
  public exportRunning: boolean = false;
  private _destroyed$: Subject<any> = new Subject();
  private selectedFilterValue: any;
  private filterName:string;
  @Input('filterSelected') filterSelectedForExport: any;

  ngOnChanges(changes: SimpleChanges): void {
    if ('filterSelectedForExport' in changes
        && changes?.filterSelectedForExport?.currentValue !== changes?.filterSelectedForExport?.previousValue) {
      this.selectedFilterValue = changes?.filterSelectedForExport?.currentValue;
    }
    this.updateConfig();
  }

  @Input('exportOptions')
  set exportDropdownOptions(value: Partial<ExportDropdownOptions>[]) {
    this._exportDropdownOptions = value;
    this.updateConfig();
  }

  get exportDropdownOptions(): Partial<ExportDropdownOptions>[] {
    return this._exportDropdownOptions;
  }

  @Input('clientChunkSize')
  set clientChunkSize(value: number) {
    this._chunkSize = value;
    this.updateConfig();
  }

  get clientChunkSize(): number {
    return this._chunkSize;
  }

  public exportDataOptions: any[] = [];

  private _exportDropdownOptions: Partial<ExportDropdownOptions>[];
  private _chunkSize: number;

  constructor(private exportService: ExportWorklistDataService,
              private authService: AuthenticationService,
              private changeDetector: ChangeDetectorRef,
              private activityService: ActivityService) {
  }

  ngAfterViewInit(): void {
    this.exportService.exportDataOptionStatusUpdateSubject
      .pipe(takeUntil(this._destroyed$.asObservable()))
      .subscribe((exportOptionStatusUpdate: ExportOptionStatusUpdate) => {
        if (exportOptionStatusUpdate.exportRunning !== undefined) {
          this.exportRunning = exportOptionStatusUpdate.exportRunning;
        }
        const exportRunning = this.exportRunning || false;
        this.setActivityStatus(exportOptionStatusUpdate.disabled, exportRunning);
        this.updateConfig();
      });
    this.updateConfig();
  }

  ngOnInit(): void {
    this.updateConfig();
  }

  ngOnDestroy() {
    this.disabled = false;
    this.disabledByFilterSelection = false;
    this.updateConfig();
    this._destroyed$.next();
    this._destroyed$.complete();
  }

  updateConfig() {
    this.exportDataOptions = this._exportDropdownOptions
      ? this.createOptions()
      : this.exportService.getExportWorklistDataOptions();
  }

  createOptions() {
    const updatedExportDropdownOptions = this._exportDropdownOptions.filter(item => {
      if (!item.permissionId) {
        return true;
      } else if (item.permissionId && this.authService.doesUserHasPermission(item.permissionId)) {
        return true;
      }
    });
    let conditionBasedDropdown: any[] = [];
    let actorId = this.authService.user.id;
    if (this.selectedFilterValue?.filter?.name !== undefined) {
      this.filterName = this.selectedFilterValue?.filter?.name;
      let isCreateSession = false;
      const enableExportOptions = (labels: string[]) => {
        updatedExportDropdownOptions.forEach(multiValue => {
          if (labels.includes(multiValue.label)) {
            this.disabled = false;
            this.disabledByFilterSelection = false;
            conditionBasedDropdown.push(multiValue);
          }
        });
      };
      this.isFilterDisabled(this.filterName, enableExportOptions,updatedExportDropdownOptions, isCreateSession, actorId);
    }
    // Check dropdown is condition Based Dropdown
    return conditionBasedDropdown.length > 0?
      this.exportService.createDataOptions(conditionBasedDropdown, this._chunkSize):
      this.exportService.createDataOptions(updatedExportDropdownOptions, this._chunkSize);
  }

  private isFilterDisabled(filterName: string, enableExportOptions?: any,
                           updatedExportDropdownOptions?: Partial<ExportDropdownOptions>[],
                           isCreateSession?: boolean,
                           actorId?: string) {
    switch (filterName) {
      case "ACR Exported":
        enableExportOptions(["Mark as confirmed by registry", "Export File"]);
        this.checkActivity(isCreateSession, actorId).pipe(takeUntil(this._destroyed$.asObservable())).subscribe( isActive =>isActive);
        break;
      case "ACR Ready For Export":
        enableExportOptions(["Export and Mark as Exported", "Export File"]);
        this.checkActivity(isCreateSession, actorId).pipe(takeUntil(this._destroyed$.asObservable())).subscribe( isActive =>isActive);
        break;
      case "Confirmed By Registry":
        this.setActivityStatus(true, false);
        break;
      case "ACR Incomplete":
        this.setActivityStatus(true, false);
        break;
      case "All":
        if (updatedExportDropdownOptions.findIndex(ele => ele['exportType'] === 'ACR') >= 0) {
          this.setActivityStatus(true, false);
        }
        break;
    }
  }

  private checkActivity(isCreateSession: boolean, actorId: string): Observable<boolean> {
    return this.activityService.searchActivity("LCO_ACR_WORKLIST_TASK", null, ActivityActions.CREATE)
      .pipe(
        take(1),
        map((activities: any[]) => {
          let sessionCreated = false;
          activities.forEach(activity => {
            const actor = get(activity, 'actor.reference', '');
            const action = get(activity, 'action', '');
            if ((actor && actor !== actorId) || action === 'create') {
              sessionCreated = true;
              isCreateSession = true;
            } else if (actor && actor === actorId && action === 'create') {
              sessionCreated = true;
              isCreateSession = true;
            }
          });
          if(this.filterName === "ACR Exported" || this.filterName === "ACR Ready For Export"){
            this.setActivityStatus(isCreateSession, sessionCreated);
          }
          return isCreateSession;
        })
      );
  }

  private setActivityStatus(status:boolean,exportRunning:boolean){
    this.disabled = status;
    this.disabledByFilterSelection = status;
    this.exportRunning = exportRunning;
    this.changeDetector.markForCheck();
  }

  getToolTipMessage(): string {
    if (this.disabled) {
      if (this.exportRunning) {
        return "Export in progress.\n\rButton will be enabled once the export is completed";
      } else {
        return "Export is disabled for current selected filter. Please select the appropriate filter to proceed";
      }
    } else {
      return "";
    }
  }
}
